import { v4 } from 'uuid'

import { GetDoctors, GetFacilities } from 'src/api/types'
import {
  IFacility,
  IPractitioner,
  IPractice,
  TSearchType,
} from 'src/shared/types'

import { ItemData } from '../types'
import { sortResults } from './sort-results'

export const formatNameWithTitle = (
  firstName: string,
  lastName: string,
  title?: string
) => `${firstName} ${lastName}${title ? `, ${title}` : ''}`

export const formatItemData = <
  Item extends
    | IPractitioner
    | IFacility
    | IPractice
    | Array<IPractitioner | IFacility | IPractice>
>(
  item: Item,
  type: TSearchType
): Item extends Array<IPractitioner | IFacility | IPractice>
  ? ItemData[]
  : ItemData => {
  if (Array.isArray(item)) {
    return item.reduce<ItemData[]>((acc, val) => {
      if (
        type === 'practice' &&
        ((val as IPractice).virtualCareOnlyCount?.only || 0) > 0 &&
        (((val as IPractice).virtualCareOnlyCount?.n || 0) !== 0 ||
          ((val as IPractice).virtualCareOnlyCount?.y || 0) !== 0)
      ) {
        const virtualOnlyCount = (val as IPractice).virtualCareOnlyCount
          ?.only as number
        const virtualOnlyItem: IPractice = {
          ...(val as IPractice),
          nbrProviders: virtualOnlyCount,
          filteredNbrProviders: virtualOnlyCount,
          virtualCareOnlyCount: {
            only: virtualOnlyCount,
          },
        }
        const virtualAvailableItem: IPractice = {
          ...(val as IPractice),
          nbrProviders: (val as IPractice).nbrProviders - virtualOnlyCount,
          filteredNbrProviders:
            (val as IPractice).filteredNbrProviders - virtualOnlyCount,
          virtualCareOnlyCount: {
            n: (val as IPractice).virtualCareOnlyCount?.n,
            y: (val as IPractice).virtualCareOnlyCount?.y,
          },
        }

        return [
          ...acc,
          formatItemData(virtualAvailableItem, type),
          formatItemData(virtualOnlyItem, type),
        ]
      }
      return [...acc, formatItemData(val, type)]
    }, [] as ItemData[]) as Item extends Array<
      IPractitioner | IFacility | IPractice
    >
      ? ItemData[]
      : ItemData
  }

  // eslint-disable-next-line no-underscore-dangle
  const id = item._id ?? item.id ?? v4()

  if (typeof item.distance === 'string') {
    item.distance = parseFloat(item.distance)
  }

  if (type === 'facility') {
    const {
      practiceName,
      distance,
      address1,
      address2,
      city,
      state,
      zipCode,
      placeId,
      phone,
      lat,
      long,
      npi,
      network,
    } = item as IFacility

    return {
      practiceName,
      distance,
      address1,
      address2,
      fullAddress: `${city}, ${state}, ${zipCode}`,
      placeId,
      phone,
      lat,
      long,
      npi,
      network,
      id,
      itemType: type,
    } as unknown as Item extends Array<IPractitioner | IFacility | IPractice>
      ? ItemData[]
      : ItemData
  }

  if (type === 'practice') {
    const {
      practiceName,
      distance,
      address1,
      address2,
      city,
      state,
      zipCode,
      placeId,
      lat,
      long,
      practicePhone,
      network,
      localUuid,
      virtualOption,
    } = item as IPractice

    const hasVirtualCare = virtualOption === 'y' || virtualOption === 'only'
    const isVirtualCareOnly = virtualOption === 'only'

    return {
      practiceName,
      distance,
      address1,
      address2,
      fullAddress: `${city}, ${state}, ${zipCode}`,
      placeId,
      phone: practicePhone,
      lat,
      long,
      network,
      localUuid,
      hasVirtualCare,
      isVirtualCareOnly,
      id,
      itemType: type,
      city,
      state,
      zipCode,
    } as unknown as Item extends Array<IPractitioner | IFacility | IPractice>
      ? ItemData[]
      : ItemData
  }

  const {
    firstName,
    lastName,
    title,
    practiceName,
    taxonomyDisplay,
    primarySpecialty,
    distance,
    address1,
    address2,
    city,
    state,
    zipCode,
    placeId,
    practicePhone,
    lat,
    long,
    npi,
    network,
    virtualOption,
    publish,
  } = item as IPractitioner

  const isVirtualCareOnly =
    !!virtualOption && virtualOption.toLowerCase().trim() === 'only'

  const hasVirtualCare =
    !!virtualOption &&
    (virtualOption.toLowerCase().trim() === 'y' ||
      virtualOption.toLowerCase().trim() === 'only')

  return {
    practiceName,
    fullName: formatNameWithTitle(firstName, lastName, title),
    distance,
    address1,
    address2,
    fullAddress: `${city}, ${state}, ${zipCode}`,
    placeId,
    phone: practicePhone,
    taxonomyDisplay,
    primarySpecialty,
    lat,
    long,
    npi,
    network,
    isVirtualCareOnly,
    hasVirtualCare,
    id,
    itemType: type,
    publish,
    city,
    state,
    zipCode,
  } as unknown as Item extends Array<IPractitioner | IFacility | IPractice>
    ? ItemData[]
    : ItemData
}

export const formatResults = (
  results: GetDoctors['data'] | GetFacilities['data'],
  type: TSearchType,
  isNameSearch?: boolean
): ItemData[] => {
  if (type === 'facility') {
    return (results as IFacility[]).map(r => formatItemData(r, type))
  }

  const { practices, providers } = results as GetDoctors['data']

  const practiceData = formatItemData(practices, 'practice')
  const providerData = formatItemData(providers, 'practitioner')

  if (isNameSearch) {
    return sortResults(practiceData.concat(providerData))
  }

  if (type === 'practice') {
    return sortResults(practiceData)
  }

  return sortResults(providerData)
}
